import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';

export interface CalendarEvent {
  id: string;
  subject: string;
  start_time: string;
  end_time: string;
  location?: string;
  teams_join_url?: string;
  organizer_email?: string;
  attendees: any[];
  is_all_day: boolean;
  body?: string;
}

export function useOutlookCalendar() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  // Het access-token van Microsoft leeft een uur, het refresh-token negentig
  // dagen. Een verlopen access-token is dus geen ontkoppeling — het is precies
  // waar het refresh-token voor bedoeld is. Deze functie oordeelde op
  // `expiry > now` en zette de agenda daarmee elk uur op "niet gekoppeld",
  // terwijl er niets stuk was. Nu telt alleen `personal_calendar_connected`,
  // en een (bijna) verlopen token wordt ververst in plaats van afgekeurd.
  //
  // De oude ondergrens `minutesUntilExpiry > 0` sloot bovendien juist het geval
  // uit waarin verversen nodig was: zodra het token echt verlopen was, viel de
  // verversing weg en bleef alleen de conclusie "niet gekoppeld" staan.
  const checkConnection = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('user_settings')
        .select('personal_calendar_connected, personal_microsoft_token_expiry')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error || !data?.personal_calendar_connected) {
        setIsConnected(false);
        return;
      }

      const verlooptOp = data.personal_microsoft_token_expiry
        ? new Date(data.personal_microsoft_token_expiry).getTime()
        : 0;
      const minutenTeGaan = (verlooptOp - Date.now()) / 60000;

      if (minutenTeGaan < 10) {
        const { error: refreshError } = await supabase.functions.invoke('outlook-refresh-token');
        if (refreshError) {
          // Hier is het wel echt op: het refresh-token is ingetrokken of
          // verlopen. Dan is opnieuw koppelen de enige weg.
          console.error('[Outlook] Verversen mislukt, opnieuw koppelen nodig:', refreshError);
          setIsConnected(false);
          return;
        }
      }

      setIsConnected(true);
    } catch (error: any) {
      console.error('[Outlook] Error checking connection:', error);
      setIsConnected(false);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    checkConnection();
  }, [checkConnection]);

  // Elke sync schrijft de afspraken naar `microsoft_calendar_events`, maar tot
  // nu toe las niemand die tabel terug: het scherm toonde uitsluitend het
  // antwoord van de zojuist gedane Graph-call. Elke maandwissel was daardoor
  // een volledige ophaalronde. Dit leest de cache en vult het scherm meteen.
  const loadCachedEvents = useCallback(async (startDate: Date, endDate: Date) => {
    if (!user) return 0;

    const { data, error } = await supabase
      .from('microsoft_calendar_events')
      .select('event_id, subject, start_time, end_time, location, teams_join_url, organizer_email, attendees, is_all_day, body')
      .eq('user_id', user.id)
      // Overlap met het venster, niet "begint in het venster". calendarView
      // geeft elke afspraak terug die het venster RAAKT, dus een afspraak van 31
      // augustus tot 2 september hoort bij september. Op alleen start_time
      // filteren liet die uit de cache vallen terwijl hij wel in het
      // Graph-antwoord zat. Dat verschil was onzichtbaar zolang de cache alleen
      // het scherm vulde vóór de sync; sinds de delta-sync vult hij het scherm
      // ook ná een sync, en dan zou zo'n afspraak bij elke delta-ronde
      // verdwijnen en bij elke volledige ronde terugkomen.
      .lte('start_time', endDate.toISOString())
      .gte('end_time', startDate.toISOString())
      .order('start_time', { ascending: true });

    if (error) {
      console.error('[Outlook] Cache lezen mislukt:', error);
      return 0;
    }

    const uitCache = (data ?? [])
      .filter((rij: any) => rij.start_time && rij.end_time)
      .map((rij: any) => ({
        // `id` is voor de rest van de app het Graph-id: deleteEvent stuurt het
        // ongewijzigd door naar Microsoft. De tabel bewaart dat in `event_id`;
        // haar eigen `id` is een lokale sleutel en zou daar niet werken.
        id: rij.event_id,
        subject: rij.subject,
        start_time: rij.start_time,
        end_time: rij.end_time,
        location: rij.location ?? undefined,
        teams_join_url: rij.teams_join_url ?? undefined,
        organizer_email: rij.organizer_email ?? undefined,
        attendees: rij.attendees ?? [],
        is_all_day: rij.is_all_day ?? false,
        body: rij.body ?? undefined,
      })) as CalendarEvent[];

    setEvents(uitCache);
    return uitCache.length;
  }, [user]);

  // `stil` onderdrukt alleen de toast: een achtergrondsync hoort niet elke
  // maandwissel een melding op te leveren.
  const syncEvents = useCallback(async (startDate: Date, endDate: Date, opties?: { stil?: boolean }) => {
    setSyncing(true);
    try {
      const { data, error } = await supabase.functions.invoke('microsoft-calendar', {
        body: {
          action: 'list',
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString()
        }
      });

      if (error) throw error;
      
      // Filter out events with invalid dates to prevent UI crashes
      const rawEvents = data.events || [];
      const validEvents = rawEvents.filter((event: CalendarEvent) => {
        if (!event.start_time || !event.end_time) {
          console.warn('[Outlook] Skipping event with missing dates:', event.subject);
          return false;
        }
        const startDate = new Date(event.start_time);
        const endDate = new Date(event.end_time);
        const isValid = !isNaN(startDate.getTime()) && !isNaN(endDate.getTime());
        if (!isValid) {
          console.warn('[Outlook] Skipping event with invalid dates:', event.subject, event.start_time, event.end_time);
        }
        return isValid;
      });
      
      const skipped = rawEvents.length - validEvents.length;

      // Een vervolgronde van de delta-sync (`partial`) bevat ALLEEN de
      // gewijzigde afspraken -- nul stuks als er niets veranderd is. Daar het
      // scherm mee vullen zou van een maand met afspraken een lege maand maken.
      // De edge function heeft de wijzigingen al in microsoft_calendar_events
      // gezet en de verwijderde eruit gehaald, dus het volledige beeld staat in
      // de cache en niet in dit antwoord.
      //
      // WAAROM DIE CACHE TE VERTROUWEN IS. Alleen omdat de edge function hem bij
      // elke niet-partiele ronde tegen Graph houdt en opruimt wat er niet meer
      // in zit (de verzoeningsstap, planWindowReconciliation in delta-logic.ts).
      // De eerste ronde van een venster is altijd niet-partieel -- er is dan nog
      // geen deltaLink --
      // en die link wordt pas bewaard nadat die verzoening is gelukt. Er bestaat
      // dus geen partiele ronde op een cache die nooit is nagekeken. Zonder die
      // stap zou een afzegging die buiten de levensduur van een deltaLink viel
      // hier permanent terugkomen in beeld: gemeten op 8 september 2026 staan er
      // 93 van de 131 rijen die sinds 2026-01-30 niet meer waren nagekeken.
      if (data.delta?.partial) {
        const visibleCount = await loadCachedEvents(startDate, endDate);
        if (!opties?.stil) {
          const verplaatst = data.delta.moved ? `, ${data.delta.moved} verplaatst` : '';
          toast({
            title: 'Agenda bijgewerkt',
            description: `${data.delta.changed} gewijzigd, ${data.delta.removed} verwijderd${verplaatst} - ${visibleCount} afspraken in beeld`
          });
        }
        return;
      }

      // Eerste ronde voor dit venster (of de volledige ophaalronde omdat er geen
      // deltaLink bewaard kon worden): dit antwoord IS het hele venster.
      setEvents(validEvents);

      if (!opties?.stil) {
        // `reconciled` telt de rijen die deze ronde uit de cache zijn gehaald
        // omdat Graph ze niet meer teruggaf. Alleen tonen als het er zijn: bij
        // een gezonde cache is dat nul en hoort er geen ruis in de melding.
        const verzoend = data.delta?.reconciled ? `, ${data.delta.reconciled} opgeruimd` : '';
        toast({
          title: 'Agenda gesynchroniseerd',
          description: `${validEvents.length} afspraken opgehaald${skipped > 0 ? ` (${skipped} overgeslagen)` : ''}${verzoend}`
        });
      }
    } catch (error: any) {
      console.error('Sync error:', error);
      toast({ 
        title: 'Synchronisatie mislukt', 
        description: error.message, 
        variant: 'destructive' 
      });
    } finally {
      setSyncing(false);
    }
  }, [toast, loadCachedEvents])

  async function createEvent(eventData: {
    subject: string;
    startTime: string;
    endTime: string;
    attendees?: string[];
    location?: string;
    body?: string;
  }) {
    try {
      const { data, error } = await supabase.functions.invoke('microsoft-calendar', {
        body: {
          action: 'create',
          ...eventData
        }
      });

      if (error) throw error;
      
      toast({ 
        title: 'Afspraak aangemaakt', 
        description: `"${eventData.subject}" is toegevoegd aan je agenda` 
      });
      return data.event;
    } catch (error: any) {
      console.error('Create event error:', error);
      toast({ 
        title: 'Fout bij aanmaken', 
        description: error.message, 
        variant: 'destructive' 
      });
      throw error;
    }
  }

  async function deleteEvent(eventId: string) {
    try {
      const { error } = await supabase.functions.invoke('microsoft-calendar', {
        body: {
          action: 'delete',
          eventId
        }
      });

      if (error) throw error;
      
      setEvents(prev => prev.filter(e => e.id !== eventId));
      toast({ 
        title: 'Afspraak verwijderd', 
        description: 'De afspraak is uit je agenda verwijderd' 
      });
    } catch (error: any) {
      console.error('Delete event error:', error);
      toast({ 
        title: 'Fout bij verwijderen', 
        description: error.message, 
        variant: 'destructive' 
      });
      throw error;
    }
  }

  return {
    events,
    isConnected,
    loading,
    syncing,
    syncEvents,
    loadCachedEvents,
    createEvent,
    deleteEvent,
    checkConnection
  };
}
